import {invalidateQueries, q} from "../../state";
import {refreshLinks} from "./tests";
import {
  AssessmentId,
  AuditArea,
  AuditAreaId,
  AuditAreaOverview,
  AuditPlan,
  AuditPlanPageActivityStreamRefs,
  AuditPlanPageId,
  AuditPlanPageStatus,
  ClusteredActivityFeed,
  ClusteredActivityFeedArgs,
  ComplexityAssessmentId,
  CreateAuditArea,
  CreateAuditAreaAssessment,
  CreateAuditAreaComplexity,
  CreateAuditAreaUnderstanding,
  CreateAuditAreasFromQuestionnaireTables,
  CreateAuditAreasFromTemplates,
  CreateReviewAction,
  EngagementId,
  ProcessCandidate,
  Reviewable,
  UnderstandingId,
} from "platformed-client/types";
import {platformedClient} from "..";

export async function get(engagementId: EngagementId): Promise<AuditPlan> {
  return platformedClient.internalRequest("get", "/region/{region}/api/tprm/engagements/{engagement_id}/audit_plan", {
    path: {engagement_id: engagementId},
    skipErrorHandler: true,
  });
}

export async function listAllAuditAreas(): Promise<AuditAreaOverview[]> {
  return platformedClient.internalRequest("get", "/region/{region}/api/tprm/audit_areas", {});
}

export async function listAuditAreas(engagementId: EngagementId): Promise<AuditArea[]> {
  return platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/engagements/{engagement_id}/audit_plan/audit_areas",
    {path: {engagement_id: engagementId}},
  );
}

export async function listReviewables(
  engagementId: EngagementId,
): Promise<Partial<Record<AuditPlanPageId, Reviewable[]>>> {
  return platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/engagements/{engagement_id}/audit_plan/reviewables",
    {path: {engagement_id: engagementId}},
  );
}

export async function create(engagementId: EngagementId): Promise<void> {
  await platformedClient.internalRequest("post", "/region/{region}/api/tprm/engagements/{engagement_id}/audit_plan", {
    path: {engagement_id: engagementId},
  });
  await invalidateQueries([q.tprm.engagementAuditPlan.filter(engagementId)]);
}

export async function createAuditArea(engagementId: EngagementId, body: CreateAuditArea): Promise<AuditPlanPageId> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/audit_plan/audit_areas",
    {path: {engagement_id: engagementId}, body},
  );
  await invalidateQueries([
    q.tprm.engagementAuditPlan.filter(engagementId),
    q.tprm.engagementAuditAreas.filter(engagementId),
  ]);
  return res;
}

export async function createAuditAreaAssessment(
  engagementId: EngagementId,
  auditAreaId: AuditAreaId,
  body: CreateAuditAreaAssessment,
): Promise<AssessmentId> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/audit_plan/audit_areas/{audit_area_id}/assessment",
    {path: {engagement_id: engagementId, audit_area_id: auditAreaId}, body},
  );
  await invalidateQueries([q.tprm.engagementAuditPlan.filter(engagementId)]);
  return res;
}

export async function createAuditAreaUnderstanding(
  engagementId: EngagementId,
  auditAreaId: AuditAreaId,
  body: CreateAuditAreaUnderstanding,
): Promise<UnderstandingId> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/audit_plan/audit_areas/{audit_area_id}/understanding",
    {path: {engagement_id: engagementId, audit_area_id: auditAreaId}, body},
  );
  await invalidateQueries([q.tprm.engagementAuditPlan.filter(engagementId)]);
  return res;
}

export async function createAuditAreaComplexity(
  engagementId: EngagementId,
  auditAreaId: AuditAreaId,
  body: CreateAuditAreaComplexity,
): Promise<ComplexityAssessmentId> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/audit_plan/audit_areas/{audit_area_id}/complexity",
    {path: {engagement_id: engagementId, audit_area_id: auditAreaId}, body},
  );
  await invalidateQueries([q.tprm.engagementAuditPlan.filter(engagementId)]);
  return res;
}

export async function createAuditAreasFromTemplates(
  engagementId: EngagementId,
  body: CreateAuditAreasFromTemplates,
): Promise<AuditPlanPageId[]> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/audit_plan/audit_areas/from_templates",
    {path: {engagement_id: engagementId}, body},
  );
  await invalidateQueries([
    q.tprm.engagementAuditPlan.filter(engagementId),
    q.tprm.engagementAuditAreas.filter(engagementId),
  ]);
  return res;
}

export async function listProcessCandidates(engagementId: EngagementId): Promise<ProcessCandidate[]> {
  return platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/engagements/{engagement_id}/process_candidates",
    {path: {engagement_id: engagementId}},
  );
}

export async function createAuditAreasFromQuestionnaireTables(
  engagementId: EngagementId,
  body: CreateAuditAreasFromQuestionnaireTables,
): Promise<AuditPlanPageId[]> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/audit_plan/audit_areas/from_questionnaire_tables",
    {path: {engagement_id: engagementId}, body},
  );
  await invalidateQueries([
    q.tprm.engagementAuditPlan.filter(engagementId),
    q.tprm.engagementAuditAreas.filter(engagementId),
  ]);
  return res;
}

export async function deletePages(engagementId: EngagementId, pageIds: AuditPlanPageId[]): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/audit_plan/pages/delete",
    {path: {engagement_id: engagementId}, body: {page_ids: pageIds}},
  );
  await Promise.all([invalidateQueries([q.tprm.engagementAuditPlan.filter(engagementId)]), refreshLinks()]);
}

export async function archivePages(engagementId: EngagementId, pageIds: AuditPlanPageId[]): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/audit_plan/pages/archive",
    {path: {engagement_id: engagementId}, body: {page_ids: pageIds}},
  );
  await invalidateQueries([q.tprm.engagementAuditPlan.filter(engagementId)]);
}

export async function unarchivePages(engagementId: EngagementId, pageIds: AuditPlanPageId[]): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/audit_plan/pages/unarchive",
    {path: {engagement_id: engagementId}, body: {page_ids: pageIds}},
  );
  await invalidateQueries([q.tprm.engagementAuditPlan.filter(engagementId)]);
}

export async function listPageActivities(
  engagementId: EngagementId,
  pageId: AuditPlanPageId,
  args: ClusteredActivityFeedArgs,
): Promise<ClusteredActivityFeed> {
  return platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/engagements/{engagement_id}/audit_plan/pages/{page_id}/activities",
    {path: {engagement_id: engagementId, page_id: pageId}, query: args},
  );
}

export async function listPageActivityStreamRefs(
  engagementId: EngagementId,
  pageId: AuditPlanPageId,
): Promise<AuditPlanPageActivityStreamRefs> {
  return platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/engagements/{engagement_id}/audit_plan/pages/{page_id}/activity_stream_ids",
    {path: {engagement_id: engagementId, page_id: pageId}},
  );
}

export async function updatePageStatus(
  engagementId: EngagementId,
  pageId: AuditPlanPageId,
  status: AuditPlanPageStatus,
): Promise<void> {
  await platformedClient.internalRequest(
    "patch",
    "/region/{region}/api/tprm/engagements/{engagement_id}/audit_plan/pages/{page_id}/status",
    {path: {engagement_id: engagementId, page_id: pageId}, body: {status}},
  );
  await invalidateQueries([q.tprm.engagementAuditPlan.filter(engagementId)]);
}

/**
 * Cascade scope for a batch of review actions. Additions and removals are scoped independently,
 * so one can reach the subtree while the other stays on the page being edited.
 */
export type ReviewCascade = {
  /** Also request the reviews on every descendant page. */
  includeChildren?: boolean;
  /** Also remove the reviewers from every descendant page. */
  removeFromChildren?: boolean;
};

export async function createReviews(
  engagementId: EngagementId,
  pageId: AuditPlanPageId,
  requests: CreateReviewAction[],
  cascade: ReviewCascade = {},
): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/audit_plan/pages/{page_id}/reviews",
    {
      path: {engagement_id: engagementId, page_id: pageId},
      query: {
        include_children: cascade.includeChildren ?? false,
        remove_from_children: cascade.removeFromChildren ?? false,
      },
      body: requests,
    },
  );
  // Refresh the plan (the page's reviewable_id may have just been created) and the reviewable itself.
  await invalidateQueries([q.tprm.engagementAuditPlan.filter(engagementId), q.reviewable.filter()]);
}

export async function lockPages(engagementId: EngagementId, pageIds: AuditPlanPageId[]): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/audit_plan/pages/lock",
    {path: {engagement_id: engagementId}, body: {page_ids: pageIds}},
  );
  await invalidateQueries([q.tprm.engagementAuditPlan.filter(engagementId)]);
}

export async function unlockPages(
  engagementId: EngagementId,
  pageIds: AuditPlanPageId[],
  includeChildren?: boolean,
): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/audit_plan/pages/unlock",
    {path: {engagement_id: engagementId}, body: {page_ids: pageIds, include_children: includeChildren ?? false}},
  );
  await invalidateQueries([q.tprm.engagementAuditPlan.filter(engagementId)]);
}
